import * as angular from 'angular';
import * as _ from 'lodash';
import * as d3 from 'd3';
import * as Widgets from '../../widgets';

import { Dashboard, DashboardConfiguration, DashboardUsers, DashboardResultDto } from '../../models/';
import { IRequestService } from '../request.service';
import { ICacheService, ILoggerService, ISessionStorageService } from '../general';
import { IWidgetSerializationService } from '../helpers';
import { DashboardThumbnailWidget } from '../../widgets/dashboardThumbnail/dashboardThumbnail.widget';
import { IIdentifierVmService } from '..';

export interface IDashboardService {
    getAll(): angular.IPromise<DashboardThumbnailWidget[]>;
    getAll(force: boolean, onlyDashboards: boolean): angular.IPromise<Dashboard[] | DashboardThumbnailWidget[]>;
    getAllUnfiltered(): angular.IPromise<Dashboard[]>;
    getAllUnfiltered(force: boolean): angular.IPromise<Dashboard[]>;
    getDashboard(guid: string): angular.IPromise<Dashboard>;
    getHomePage(): angular.IPromise<DashboardThumbnailWidget[]>;
    addDashboard(dashboard: any, filter?: boolean): angular.IPromise<any>;
    addDashboards(dashboards: any[], maxRow: number, appName?: string, filter?: boolean): angular.IPromise<any>;
    addDashboardToApplicationByName(dashboard: any, name: string, filter?: boolean): angular.IPromise<any>;
    duplicateDashboard(guid: string, filter?: boolean): angular.IPromise<any>;
    updateDashboard(dashboard: any, filter?: boolean): angular.IPromise<any>;
    updateDashboards(dashboard: any[], filter?: boolean): angular.IPromise<DashboardThumbnailWidget[]>;
    deleteDashboard(guid: string): angular.IPromise<Dashboard[]>;
    deleteDashboards(guid: string[]): angular.IPromise<Dashboard[]>;

    addWidget(dashboardGuid: string, widget: Widgets.IWidget): angular.IPromise<any>;
    updateWidget(guid: string, widget: any): angular.IPromise<any>;
    deleteWidget(dashboardGuid: string, widgetGuid: string): angular.IPromise<any>;
    cloneMany(dashboard: DashboardThumbnailWidget[]): DashboardThumbnailWidget[];

    getDashboardThumbnails(data: any[]): DashboardThumbnailWidget[];
    deserializeDashboard(item: Dashboard): Dashboard;
    serializeDashboard(item: Dashboard): any;

    getAllUsers(): angular.IPromise<DashboardUsers>;
    getAllUsers(force: boolean): angular.IPromise<DashboardUsers>;
}

export class DashboardService implements IDashboardService {
    static serviceId = 'dashboardService';
    private _controllerName = 'dashboard/';
    private _controllerUrl: string;

    static $inject = [
        '$q',
        'loggerService',
        'requestService',
        'sessionStorageService',
        'widgetSerializationService',
        'identifierVmService',
        'cacheService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _loggerService: ILoggerService,
        private _requestService: IRequestService,
        private _storageService: ISessionStorageService,
        private _widgetSerializationService: IWidgetSerializationService,
        private _identifierVmService: IIdentifierVmService,
        private _cache: ICacheService
    ) {
    }

    private controllerUrl() {
        if (!this._controllerUrl) {
            this._controllerUrl = this._requestService.getUrlV1() + this._controllerName;
        }
        return this._controllerUrl;
    }

    getDashboard(guid: string): angular.IPromise<Dashboard> {
        const deferred = this._$q.defer<Dashboard>();
        try {
            if (guid) {
                const stored = this._storageService.getTypedFrom<Dashboard>(guid);
                if (!stored) {
                    this._requestService
                        .getAtV1<Dashboard>(`${this._controllerName}${guid}`, undefined, true)
                        .then(data => deferred.resolve(this.deserializeDashboard(data)))
                        .catch(reason => deferred.reject(reason));
                } else {
                    deferred.resolve(this.deserializeDashboard(stored));
                }
            } else {
                this.getAll()
                    .then((data) => deferred.resolve(data))
                    .catch(reason => deferred.reject(reason));
            }
        } catch (e) {
            this._loggerService.logError(e);
            deferred.reject();
        }
        return deferred.promise;
    }

    getAll(force?: boolean, onlyDashboards: boolean = false): angular.IPromise<any> {
        if (force) {
            this._requestService.cleanCache(this.controllerUrl());
        }
        return this._requestService
            .getAtV1<Dashboard[]>(`${this._controllerName}`, undefined, true)
            .then(data => {
                data.forEach(d => this._storageService.setTypedTo(d.guid, d));
                let result: any[] = [];
                if (!onlyDashboards) {
                    result = this.getDashboardThumbnails(data);
                } else {
                    result = data;
                }
                return result;
            });
    }

    getAllUnfiltered(): angular.IPromise<Dashboard[]>;
    getAllUnfiltered(force: boolean): angular.IPromise<Dashboard[]>;
    getAllUnfiltered(force?: boolean): angular.IPromise<Dashboard[]> {
        if (force) {
            this._requestService.cleanCacheAtV1(this._controllerName, { filter: false });
        }
        return this._requestService.getAtV1(this._controllerName, { filter: false }, true);
    }

    getHomePage(): angular.IPromise<DashboardThumbnailWidget[]> {
        return this._requestService.getAtV1<any[]>(this._controllerName + 'homePage/', undefined, true)
            .then(data => {
                data.forEach(d => this._storageService.setTypedTo(d.guid, d));
                return this.getDashboardThumbnails(data);
            });
    }

    addDashboardToApplicationByName(dashboard, applicationName, filter?: boolean): angular.IPromise<any> {
        const url = this.controllerUrl() + applicationName + '/' + filter + '/';
        const deferred = this._$q.defer();

        this.addOrUpdate(dashboard, url)
            .then((data) => {
                this._requestService.cleanCache(url);
                deferred.resolve(data);
            }, reason => {
                deferred.reject(reason);
            });

        return deferred.promise;
    }

    addDashboard(dashboard: any, filter?: boolean): angular.IPromise<any> {
        const url = this.controllerUrl() + '/' + filter + '/';
        const deferred = this._$q.defer();
        this.getAll()
            .then(allDashboards => {
                dashboard.row = this.getMaxRow(allDashboards);
                this.addOrUpdate(dashboard.dashboard || dashboard, url)
                    .then(result => {
                        this._requestService.cleanCache(url);
                        deferred.resolve(result);
                    }, reason => {
                        deferred.reject(reason);
                    });
            });
        return deferred.promise;
    }

    addDashboards(dashboards: any[], maxRow: number, appName?: string, filter?: boolean): angular.IPromise<any> {
        let url = this.controllerUrl() + 'addAll/';
        if (appName) {
            url = url + appName;
        }
        if (filter) {
            url = `${url}/${filter}`;
        }
        let promise: angular.IPromise<any>;
        const toAdd: Dashboard[] = dashboards.map(d => d.dashboard);
        toAdd.forEach((dashboard, index) => {
            dashboard.row = maxRow + index;
            try {
                if (dashboard.configuration && dashboard.configuration.widgets) {
                    dashboard.configuration.widgets = dashboard.configuration.widgets
                        .map(w => this._widgetSerializationService.serializeWidget(w));
                }
            } catch (e) {
                this._loggerService.logError(e);
                promise = this._$q.when([]);
            }
        });
        promise = this._requestService.post(url, toAdd, undefined, true)
            .then(data => {
                this._requestService.cleanCache(url);
                this._requestService.cleanCache(this.controllerUrl() + 'homePage/');
                return data;
            });
        return promise;
    }

    duplicateDashboard(guid: string, filter?: boolean): angular.IPromise<any> {
        const url = this.controllerUrl() + 'duplicate/' + guid + '/' + filter + '/';
        const deferred = this._$q.defer();
        this._requestService.post(url, undefined, undefined, true)
            .then((data) => {
                this._loggerService.logSuccess('Dashboard successfully duplicated.');
                const list = this.getDashboardThumbnails(data);
                this._requestService.cleanCache(this.controllerUrl());
                this.cleanDashboardSeriesCache();
                deferred.resolve(list);
            }, () => {
                this._loggerService.logError('Error while duplicating dashboard.');
                deferred.reject();
            });
        return deferred.promise;
    }

    updateDashboard(dashboard: any, filter?: boolean): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'dashboard/update/' + filter + '/';
        return this.addOrUpdate(dashboard, url);
    }

    updateDashboards(dashboardList: DashboardThumbnailWidget[], filter?: boolean): angular.IPromise<any> {
        const defered = this._$q.defer();
        if (dashboardList) {
            const url = this._requestService.getUrlV1() + 'dashboard/updateAll/' + filter + '/';
            const toSave = [];
            try {
                dashboardList.forEach(d => {
                    const dashboard = new Dashboard();
                    dashboard.backgroundUrl = d.dashboard.backgroundUrl;
                    dashboard.categories = angular.copy(d.dashboard.categories);
                    dashboard.col = d.col;
                    dashboard.configuration = d.configuration as DashboardConfiguration;
                    dashboard.dashboardLink = d.dashboard.dashboardLink;
                    dashboard.defaultPeriod = d.dashboard.defaultPeriod;
                    dashboard.documentation = d.dashboard.documentation;
                    dashboard.groupGuids = d.dashboard.groupGuids?.map(d => d);
                    dashboard.groupTypes = d.dashboard.groupTypes;
                    dashboard.guid = d.dashboard.guid;
                    dashboard.isAutoRefresh = d.dashboard.isAutoRefresh;
                    dashboard.isDatePickerDisabled = d.dashboard.isDatePickerDisabled;
                    dashboard.isExportToChildApplication = d.dashboard.isExportToChildApplication;
                    dashboard.isHomePage = d.dashboard.isHomePage;
                    dashboard.name = d.dashboard.name;
                    dashboard.referenceGroup = d.dashboard.referenceGroup;
                    dashboard.row = d.row;
                    dashboard.shortComment = d.dashboard.shortComment;
                    dashboard.sortOrder = d.dashboard.sortOrder;
                    toSave.push(this.serializeDashboard(dashboard));
                });
            } catch (e) {
                this._loggerService.logError('Error while serializing dashboards.');
                defered.reject();
            }
            if (toSave.length > 0) {
                const self = this;

                toSave.forEach(dashboard => {
                    dashboard.configuration.widgets = dashboard.configuration.widgets
                        .map(w => this._widgetSerializationService.serializeWidget(w));
                });

                this._requestService.post(url, toSave, undefined, true)
                    .then((data) => {
                        this._requestService.cleanCache(this.controllerUrl());
                        dashboardList.forEach(d => this.cleanDashboardData(d.guid));
                        const thumbs = self.getDashboardThumbnails(data);
                        defered.resolve(thumbs);
                        this.cleanDashboardSeriesCache();
                    }).catch((reason) => {
                        defered.reject(reason);
                    });
            }
            return defered.promise;
        } else {
            defered.reject('Empty dashboard list');
        }
    }

    deleteDashboard(guid: string): angular.IPromise<Dashboard[]> {
        const url = this._controllerName + 'delete/' + guid + '/';
        return this._requestService
            .postAtV1<Dashboard[]>(url)
            .then(data => {
                this._loggerService.logSuccess('Dashboard successfully deleted.');
                this.cleanDashboardData(guid);
                this.cleanDashboardSeriesCache();
                return data;
            });
    }

    deleteDashboards(guids: string[]): angular.IPromise<Dashboard[]> {
        const url = this._controllerName + 'deleteAll/';
        return this._requestService
            .postAtV1<DashboardResultDto>(url, { guidList: guids })
            .then(data => {
                if (data && data.notHandledDashboardGuids.length === guids.length) {
                    this._loggerService.logSuccess('Some dashboards cannot be deleted.');
                } else {
                    this._loggerService.logSuccess('Dashboards successfully deleted.');
                }
                this.cleanDashboardsData(guids);
                this.cleanDashboardSeriesCache();
                return data.dashboards;
            })
            .catch(reason => {
                this._loggerService.logError(`Error while deleting dashboard: ${reason}`);
                return [];
            });
    }

    addWidget(dashboardGuid: string, widget: Widgets.IWidget): angular.IPromise<any> {
        const url = this.controllerUrl() + 'widget/add/' + dashboardGuid + '/';
        return this.computeWidgetPosition(dashboardGuid, widget)
            .then(() => this.addOrUpdateWidget(url, dashboardGuid, widget));
    }

    updateWidget(guid: string, widget: any): angular.IPromise<any> {
        const url = this.controllerUrl() + 'widget/update/' + guid + '/';
        return this.addOrUpdateWidget(url, guid, widget);
    }

    deleteWidget(dashboardGuid: string, widgetGuid: string): angular.IPromise<any> {
        const url = this.controllerUrl() + 'widget/delete/' + dashboardGuid + '/' + widgetGuid + '/';
        this.cleanDashboardData(dashboardGuid);
        const deferred = this._$q.defer();
        this._requestService.delete(url)
            .then(data => this.updateDashboardWidgets(data))
            .then((dashboard) => deferred.resolve(dashboard));
        return deferred.promise;
    }

    private addOrUpdateWidget(url: string, dashboardGuid: string, widget): angular.IPromise<Dashboard> {
        const deferred = this._$q.defer<Dashboard>();
        this.cleanDashboardData(dashboardGuid);
        try {
            const w = this._widgetSerializationService.serializeWidget(widget);
            this._requestService.post(url, w, undefined, true)
                .then(
                    data => {
                        widget.configuration = this._widgetSerializationService.parseConfigObject(widget.configuration);
                        return this.updateDashboardWidgets(data);
                    },
                    (error) => deferred.reject(error))
                .then(
                    (dashboard) => deferred.resolve(dashboard)
                );
        } catch (e) {
            this._loggerService.logError(e);
            deferred.reject(e);
        }
        return deferred.promise;
    }

    private updateDashboardWidgets(updatedDashboard): angular.IPromise<any> {
        const defered = this._$q.defer();
        const self = this;
        this.getDashboard(updatedDashboard.guid)
            .then(dashboard => {
                if (dashboard) {
                    dashboard.configuration.widgets = updatedDashboard.configuration.widgets;
                    self.deSerializeWidgetsConfiguration(dashboard);
                    defered.resolve(dashboard);
                }
            });
        return defered.promise;
    }

    private addOrUpdate(dashboard, url): angular.IPromise<any[]> {
        const self = this;
        const deferred = this._$q.defer<any[]>();
        try {
            if (dashboard.configuration && dashboard.configuration.widgets) {
                dashboard.configuration.widgets = dashboard.configuration.widgets
                    .map(w => this._widgetSerializationService.serializeWidget(w));
            }

            this._requestService.post(url, dashboard, undefined, true)
                .then((data: any[]) => {
                    this.deSerializeWidgetsConfiguration(dashboard);
                    this.cleanDashboardData(dashboard.guid);
                    const newDashboard = _.find(data, d => d.guid === dashboard.guid);
                    if (newDashboard) {
                        this._storageService.setTypedTo(newDashboard.guid, newDashboard);
                    }
                    this._requestService.cleanCache(this.controllerUrl() + 'homePage/');
                    this.cleanDashboardSeriesCache();
                    const thumbs = self.getDashboardThumbnails(data);
                    deferred.resolve(thumbs);
                });
        } catch (e) {
            this._loggerService.logError(e);
            deferred.reject('Error while adding dashboard.');
        }

        return deferred.promise;
    }

    getDashboardThumbnails(data: Dashboard[]): DashboardThumbnailWidget[] {
        const sizeX = 3;
        const sizeY = 6;
        let dashboards = [];

        data.forEach(d => {
            const temporaryDashboard: Dashboard = Dashboard.cloneWithoutConfiguration(d);

            if (!temporaryDashboard.configuration) {
                temporaryDashboard.configuration = {
                    widgets: [],
                    printConfiguration: d.configuration.printConfiguration
                } as DashboardConfiguration;
            }
            temporaryDashboard.configuration.type = 'dashboard';
            temporaryDashboard.configuration = DashboardConfiguration.copy(d.configuration);
            if (temporaryDashboard.col === null || temporaryDashboard.col === undefined) {
                temporaryDashboard.col = 0;
                temporaryDashboard.row = this.getMaxRow(dashboards);
            }
            const typedWidget = this._widgetSerializationService.createTypedWidget(temporaryDashboard, 'dashboardThumbnail');
            typedWidget.sizeX = sizeX;
            typedWidget.sizeY = sizeY;
            typedWidget.isTitleHidden = d.isTitleHidden;
            typedWidget.dashboard = d;
            dashboards.push(typedWidget);
            this.deSerializeWidgetsConfiguration(temporaryDashboard);
        });

        dashboards = _.sortBy(dashboards, d => d.row);
        dashboards = _.chain(dashboards).groupBy(d => d.row)
            .map((r: any) => { return _.sortBy(r, (row: any) => row.col); })
            .flatten()
            .value();

        return dashboards;
    }

    private deSerializeWidgetsConfiguration(dashboard: any) {
        if (dashboard && dashboard.configuration && dashboard.configuration.widgets) {
            const typedWidgets = [];
            const self = this;
            dashboard.configuration.widgets.forEach(w => {
                const widget = self._widgetSerializationService.createTypedWidget(w);
                if (widget) {
                    typedWidgets.push(widget);
                }
            });
            dashboard.configuration.widgets = typedWidgets;
        }
    }

    private cleanDashboardData(guid: string): void {
        this._requestService.cleanCache(this.controllerUrl() + guid);
        this._requestService.cleanCache(this.controllerUrl() + guid + '/');
        this._requestService.cleanCache(this.controllerUrl());
        this._requestService.cleanCacheAtV1(this._controllerName + 'delete/' + guid);
        this._requestService.cleanCacheAtV1(this._controllerName + 'deleteAll/');
        this._requestService.cleanCacheAtV1(this._controllerName);
        this._storageService.remove(guid);
    }

    private cleanDashboardsData(guids: string[]): void {
        guids.forEach(guid => this.cleanDashboardData(guid));
    }

    private cleanDashboardSeriesCache(): void {
        this._identifierVmService.cleanCompleteCache();
    }

    computeWidgetPosition(dashboardGuid: string, widget: Widgets.IWidget): angular.IPromise<any> {
        const defered = this._$q.defer();
        const self = this;
        this.getDashboard(dashboardGuid).then(dashboard => {
            try {
                if (dashboard.configuration && dashboard.configuration.widgets.length > 0) {
                    widget.row = self.getMaxRow(dashboard.configuration.widgets);
                }
                defered.resolve();
            } catch (e) {
                defered.reject(e);
            }
        });
        return defered.promise;
    }

    private getMaxRow(widgetList: Widgets.IWidget[]): number {
        const maxRow = d3.max(widgetList, (w: Widgets.IWidget) => w.row + w.sizeY);
        return maxRow + 1;
    }

    serializeDashboard(item: Dashboard): any {
        const result: any = {
            backgroundUrl: item.backgroundUrl,
            categories: item.categories,
            col: item.col,
            dashboardLink: item.dashboardLink,
            defaultPeriod: item.defaultPeriod,
            documentation: item.documentation,
            groupGuids: item.groupGuids?.map(d => d) ?? [],
            groupTypes: item.groupTypes,
            guid: item.guid,
            id: item.id,
            isAutoRefresh: item.isAutoRefresh,
            isDatePickerDisabled: item.isDatePickerDisabled,
            isExportToChildApplication: item.isExportToChildApplication,
            isHomePage: item.isHomePage,
            isTitleHidden: item.isTitleHidden,
            name: item.name,
            referenceGroup: item.referenceGroup,
            row: item.row,
            shortComment: item.shortComment,
            sortOrder: item.sortOrder,
            url: item.url
        };
        result.configuration = DashboardConfiguration.serialize(item.configuration);
        return result;
    }

    deserializeDashboard(item: Dashboard): Dashboard {
        const result = angular.copy(item);
        try {
            _.remove(result.configuration.widgets, () => true);
            item.configuration.widgets.forEach(w => {
                const typedWidget = this._widgetSerializationService.createTypedWidget(w);
                if (typedWidget) {
                    result.configuration.widgets.push(typedWidget);
                }
            });
        } catch (e) {
            this._loggerService.logError(e);
        }
        return result;
    }

    cloneMany(dashboards: DashboardThumbnailWidget[]): DashboardThumbnailWidget[] {
        return this.getDashboardThumbnails(dashboards.map(d => this.deserializeDashboard(d.dashboard.serialize())));
    }

    getAllUsers(): angular.IPromise<DashboardUsers>;
    getAllUsers(force: boolean): angular.IPromise<DashboardUsers>;
    getAllUsers(force?: boolean): angular.IPromise<DashboardUsers> {
        const url = 'dashboard/users/';
        if (force) {
            this._requestService.cleanCacheAtV1(url);
        }
        return this._requestService.getAtV1<DashboardUsers>(url, undefined, true);
    }
}